// Parse API errors into user-friendly messages

import type { ApiError } from '../types'

export function parseApiError(error: unknown): ApiError {
  // Network error
  if (error instanceof TypeError && error.message === 'Failed to fetch') {
    return {
      message: 'Unable to connect to the server. Please check your internet connection.',
    }
  }

  // Response-based error
  if (error && typeof error === 'object' && 'status' in error) {
    const err = error as { status: number; data?: Record<string, unknown> }
    const status = err.status

    if (status === 400) {
      const data = err.data
      if (data?.error && typeof data.error === 'string') {
        return { message: data.error, status }
      }
      // Structured error object: { error: { code, message } }
      if (
        data?.error &&
        typeof data.error === 'object' &&
        'message' in (data.error as Record<string, unknown>)
      ) {
        const errorObj = data.error as { code?: string; message: string }
        return { message: errorObj.message, code: errorObj.code, status }
      }
      // Field-level errors from DRF
      if (data) {
        const firstField = Object.keys(data).find(k => k !== 'error')
        if (firstField) {
          const fieldError = data[firstField]
          const message = Array.isArray(fieldError) ? fieldError[0] : String(fieldError)
          return { message, field: firstField, status }
        }
      }
      return { message: 'Invalid request. Please check your input.', status }
    }

    if (status === 401) {
      return { message: 'Session expired. Please sign in again.', status }
    }

    if (status === 403) {
      return { message: 'You do not have permission to perform this action.', status }
    }

    if (status === 404) {
      return { message: 'The requested resource was not found.', status }
    }

    if (status === 409) {
      // Conflict (e.g. an order already actioned by another approver). Prefer
      // the backend's specific message so the caller can surface it.
      const data = err.data
      if (data?.error && typeof data.error === 'string') {
        return { message: data.error, status }
      }
      return { message: 'This action is no longer available.', status }
    }

    if (status >= 500) {
      // Surface the backend's real error when it provides one (DEBUG includes a
      // `details` field with the actual exception); otherwise fall back to the
      // generic message.
      const errorObj = err.data?.error
      if (errorObj && typeof errorObj === 'object') {
        const structured = errorObj as { message?: string; code?: string; details?: string }
        const message = structured.details || structured.message
        if (message) {
          return { message, code: structured.code, status }
        }
      }
      return { message: 'A server error occurred. Please try again later.', status }
    }

    return { message: 'An unexpected error occurred.', status }
  }

  // Generic error
  if (error instanceof Error) {
    return { message: error.message }
  }

  return { message: 'An unexpected error occurred.' }
}
